import type { Bi } from 'schools/suryaSiddhanta/types/bilingual';

export type SuryaSiddhantaTopic = {
  id: string;
  title: Bi;
  summary: Bi;
  readMinutes: number;
  icon: string;
};

export type SuryaSiddhantaChapter = {
  id: string;
  number: number;
  title: Bi;
  subtitle: Bi;
  icon: string;
  topics: SuryaSiddhantaTopic[];
};

export const SURYA_SIDDHANTA_HERO = {
  eyebrow: { en: 'Ganita Jyotisha · Siddhanta', hi: 'गणित ज्योतिष · सिद्धान्त' },
  titleShort: { en: 'Surya Siddhanta', hi: 'सूर्य सिद्धान्त' },
  title: { en: 'Surya Siddhanta', hi: 'सूर्य सिद्धान्त' },
  motto: {
    en: 'Classical astronomical foundations - Mahayuga periods, lunar time, and the math behind Panchang.',
    hi: 'शास्त्रीय खगोलीय नींव - महायुग काल, चंद्र-समय, और पंचांग के पीछे का गणित।',
  },
  tagline: {
    en: 'Twenty-four lessons: derive the solar year, sidereal month, planetary periods, Lahiri ayanamsha, and Panchang limbs from first principles.',
    hi: 'चौबीस पाठ: सौर वर्ष, नक्षत्र मास, ग्रह-काल, लाहिरी अयनांश और पंचांग अंग - मूल सिद्धान्त से।',
  },
  intro: {
    en: 'Calculate first, interpret later. This school teaches Ganita (astronomy) - the layer Phalita Jyotish rests upon.',
    hi: 'पहले गणना, बाद में व्याख्या। यह स्कूल गणित (खगोल) सिखाता है - जिस पर फलित ज्योतिष टिका है।',
  },
  trait: {
    en: 'Mahayuga ÷ revolutions = orbital period',
    hi: 'महायुग ÷ क्रान्तियाँ = परिक्रमा-काल',
  },
};

function topic(
  id: string,
  titleEn: string,
  titleHi: string,
  summaryEn: string,
  summaryHi: string,
  readMinutes: number,
  icon: string,
): SuryaSiddhantaTopic {
  return {
    id,
    title: { en: titleEn, hi: titleHi },
    summary: { en: summaryEn, hi: summaryHi },
    readMinutes,
    icon,
  };
}

export const SURYA_SIDDHANTA_CHAPTERS: SuryaSiddhantaChapter[] = [
  {
    id: 'introduction',
    number: 1,
    title: { en: 'Introduction', hi: 'परिचय' },
    subtitle: {
      en: 'What the Surya Siddhanta is and where Ganita sits in Jyotish',
      hi: 'सूर्य सिद्धान्त क्या है और ज्योतिष में गणित कहाँ बैठता है',
    },
    icon: '☀️',
    topics: [
      topic('what-is-surya-siddhanta', 'What Is the Surya Siddhanta?', 'सूर्य सिद्धान्त क्या है?', 'Scope, lineage, and Ganita vs religious reading.', 'क्षेत्र, परंपरा, गणित बनाम धार्मिक पठन।', 17, '☀️'),
      topic('ganita-vs-phalita', 'Ganita vs Phalita', 'गणित बनाम फलित', 'Astronomy paves the road; prediction walks on it.', 'खगोल ने रास्ता बनाया; भविष्य उस पर चला।', 18, '🔭'),
      topic('siddhanta-in-jyotish-branches', 'Siddhanta in the Three Limbs', 'तीन अंगों में सिद्धान्त', 'Ganita, Samhita, Hora - where each lives.', 'गणित, संहिता, होरा - कौन कहाँ।', 16, '📐'),
    ],
  },
  {
    id: 'mahayuga-time',
    number: 2,
    title: { en: 'Mahayuga & Time', hi: 'महायुग और काल' },
    subtitle: {
      en: 'Four Yugas, total days, and the famous solar year derivation',
      hi: 'चार युग, कुल दिन, और प्रसिद्ध सौर वर्ष-निर्देश',
    },
    icon: '⏳',
    topics: [
      topic('four-yugas-and-ratio', 'Four Yugas & the 4:3:2:1 Ratio', 'चार युग और 4:3:2:1 अनुपात', 'Base 432,000 and Mahayuga total.', 'आधार 4,32,000 और महायुग योग।', 18, '4️⃣'),
      topic('mahayuga-days-and-years', 'Mahayuga Days & Years', 'महायुग दिन और वर्ष', '1,577,917,828 days in one Mahayuga.', '1 महायुग = 1,57,91,78,828 दिन।', 17, '📅'),
      topic('deriving-solar-year', 'Deriving the Solar Year', 'सौर वर्ष की गणना', '365.2588 days - divide total days by solar years.', '365.2588 दिन - कुल दिन ÷ सौर वर्ष।', 19, '🌍'),
      topic('cosmic-cycles-overview', 'Cosmic Cycles Overview', 'ब्रह्मांडीय चक्र अवलोकन', 'Manvantara, Kalpa, and the 432 pattern.', 'मन्वन्तर, कल्प, और 432 पैटर्न।', 18, '🌌'),
    ],
  },
  {
    id: 'moon-nakshatra',
    number: 3,
    title: { en: 'Moon & Nakshatra', hi: 'चंद्र और नक्षत्र' },
    subtitle: {
      en: 'Sidereal month, synodic month, and the 27-Nakshatra arc',
      hi: 'नक्षत्र मास, सौर मास, और 27-नक्षत्र चाप',
    },
    icon: '🌙',
    topics: [
      topic('sidereal-month-derivation', 'Sidereal Month Derivation', 'नक्षत्र मास की गणना', '57,753,336 revolutions → ~27.32 days.', '5,77,53,336 क्रान्तियाँ → ~27.32 दिन।', 19, '🌙'),
      topic('sidereal-vs-synodic-month', 'Sidereal vs Synodic Month', 'नक्षत्र बनाम सौर मास', '27.32 vs 29.53 - Nakshatra vs Tithi.', '27.32 बनाम 29.53 - नक्षत्र बनाम तिथि।', 18, '🔀'),
      topic('nakshatra-arc-and-daily-motion', 'Nakshatra Arc & Daily Motion', 'नक्षत्र चाप और दैनिक गति', '360° ÷ 27 = 13°20′; ~1 day per Nakshatra.', '360° ÷ 27 = 13°20′; ~1 दिन प्रति नक्षत्र।', 17, '⭐'),
      topic('nakshatra-jyotish-bridge', 'Bridge to Nakshatra Jyotish', 'नक्षत्र ज्योतिष कड़ी', 'Why daily Nakshatra changes; link to Panchang school (later).', 'नक्षत्र रोज क्यों बदलता है; पंचांग स्कूल कड़ी (बाद में)।', 16, '🔗'),
    ],
  },
  {
    id: 'planetary-periods',
    number: 4,
    title: { en: 'Planetary Periods', hi: 'ग्रह-काल' },
    subtitle: {
      en: 'Mars, Jupiter, Saturn, Mercury, Venus from one Mahayuga formula',
      hi: 'एक महायुग सूत्र से मंगल, गुरु, शनि, बुध, शुक्र',
    },
    icon: '🪐',
    topics: [
      topic('planetary-periods-overview', 'Planetary Periods Overview', 'ग्रह-काल अवलोकन', 'One formula: total days ÷ revolutions.', 'एक सूत्र: कुल दिन ÷ क्रान्तियाँ।', 17, '🪐'),
      topic('mars-jupiter-saturn-periods', 'Mars, Jupiter & Saturn', 'मंगल, गुरु और शनि', '687 days, 11.86 years, 29.5 years.', '687 दिन, 11.86 वर्ष, 29.5 वर्ष।', 20, '♂️'),
      topic('mercury-venus-periods', 'Mercury & Venus', 'बुध और शुक्र', 'Swift inner planets ~88 and ~225 days.', 'तीव्र अंतर ग्रह ~88 और ~225 दिन।', 18, '☿️'),
      topic('planet-sign-transit-memory', 'Planet Sign Transit Memory', 'ग्रह-राशि गति स्मरण', 'Moon 1d, Sun 1mo, Guru 1yr, Shani 2.5yr per sign.', 'चंद्र 1दिन, सूर्य 1माह, गुरु 1वर्ष, शनि 2.5वर्ष/राशि।', 17, '📋'),
      topic('planetary-periods-workshop', 'Planetary Periods Workshop', 'ग्रह-काल कार्यशाला', 'Five planets in fifteen minutes.', 'पंद्रह मिनट में पाँच ग्रह।', 19, '🛠️'),
    ],
  },
  {
    id: 'ayanamsha-live-sky',
    number: 5,
    title: { en: 'Ayanamsha & Live Sky', hi: 'अयनांश और जीवित आकाश' },
    subtitle: {
      en: 'Precession, Lahiri ayanamsha, and Panchang limb preview',
      hi: 'परिक्रमण, लाहिरी अयनांश, और पंचांग अंग पूर्वावलोकन',
    },
    icon: '🌐',
    topics: [
      topic('precession-and-ayanamsha', 'Precession & Ayanamsha', 'परिक्रमण और अयनांश', 'Tropical vs sidereal zodiac; why ayanamsha exists.', 'सायन बनाम निरयन; अयनांश क्यों।', 18, '🌐'),
      topic('lahiri-ayanamsha-live', 'Lahiri Ayanamsha (Live)', 'लाहिरी अयनांश (लाइव)', 'Date → Lahiri degrees via ephemeris.', 'तिथि → लाहिरी अंश (एफेमेरिस)।', 19, '📡'),
      topic('sun-moon-longitudes', 'Sun & Moon Longitudes', 'सूर्य-चंद्र देशांतर', 'Datetime + place → sidereal longitudes.', 'समय + स्थान → निरयन देशांतर।', 19, '☀️'),
      topic('panchang-limbs-preview', 'Panchang Limbs Preview', 'पंचांग अंग पूर्वावलोकन', 'Tithi + Nakshatra from longitudes; full Panchang school later.', 'देशांतर से तिथि + नक्षत्र; पूर्ण पंचांग बाद में।', 18, '📿'),
    ],
  },
  {
    id: 'bridge-capstone',
    number: 6,
    title: { en: 'Bridge & Capstone', hi: 'कड़ी और समापन' },
    subtitle: {
      en: 'From Siddhanta to ephemeris, modern comparison, and integrated worksheet',
      hi: 'सिद्धान्त से एफेमेरिस, आधुनिक तुलना, और एकीकृत वर्कशीट',
    },
    icon: '🎓',
    topics: [
      topic('siddhanta-to-ephemeris-flow', 'Siddhanta → Ephemeris Flow', 'सिद्धान्त → एफेमेरिस प्रवाह', 'How Ganita feeds charts and Panchang.', 'गणित कुंडली और पंचांग को कैसे खिलाता है।', 17, '🔁'),
      topic('ss-vs-modern-astronomy', 'SS vs Modern Astronomy', 'सिद्धान्त बनाम आधुनिक खगोल', 'Compare every derived period to NASA values.', 'हर निकाला काल NASA मान से तुलना।', 18, '🔬'),
      topic('reading-sanskrit-numerals', 'Reading Sanskrit Numerals', 'संस्कृत अंक पढ़ना', 'Devanagari digits for classical texts.', 'शास्त्रीय ग्रंथों हेतु देवनागरी अंक।', 16, '🔤'),
      topic('integrated-ganita-capstone', 'Integrated Ganita Capstone', 'एकीकृत गणित समापन', 'Full worksheet: Mahayuga + planet + live sky.', 'पूर्ण वर्कशीट: महायुग + ग्रह + जीवित आकाश।', 22, '🎓'),
    ],
  },
];

export function getSuryaSiddhantaTopic(id: string) {
  for (const chapter of SURYA_SIDDHANTA_CHAPTERS) {
    const topic = chapter.topics.find((t) => t.id === id);
    if (topic) return { topic, chapter };
  }
  return null;
}

export function getSuryaSiddhantaChapter(chapterId: string): SuryaSiddhantaChapter | undefined {
  return SURYA_SIDDHANTA_CHAPTERS.find((c) => c.id === chapterId);
}

export function getSuryaSiddhantaChapterIndex(chapterId: string): number {
  return SURYA_SIDDHANTA_CHAPTERS.findIndex((c) => c.id === chapterId);
}

export function getSuryaSiddhantaTopicCount(): number {
  return SURYA_SIDDHANTA_CHAPTERS.reduce((n, c) => n + c.topics.length, 0);
}

export function getSuryaSiddhantaTopicNeighbors(topicId: string): {
  prev: SuryaSiddhantaTopic | null;
  next: SuryaSiddhantaTopic | null;
} {
  const flat = SURYA_SIDDHANTA_CHAPTERS.flatMap((c) => c.topics);
  const idx = flat.findIndex((t) => t.id === topicId);
  if (idx < 0) return { prev: null, next: null };
  return { prev: flat[idx - 1] ?? null, next: flat[idx + 1] ?? null };
}

export function getSuryaSiddhantaSearchTerms(): Array<{ id: string; terms: string }> {
  return SURYA_SIDDHANTA_CHAPTERS.flatMap((ch) =>
    ch.topics.map((t) => ({
      id: t.id,
      terms: [t.title.en, t.title.hi, t.summary.en, t.summary.hi, ch.title.en, ch.title.hi].join(' '),
    })),
  );
}
